interface RowSelectZoneProps {
  checked: boolean;
  onToggle: () => void;
  /** Accessible label for the checkbox, e.g. "Select expense". */
  ariaLabel?: string;
  /**
   * Extra classes for the tap zone. Defaults target the standard approval-card
   * padding (px-4 sm:px-5 py-3.5) so the zone bleeds to the card edges and the
   * divider spans the full card height. Override for cards with other padding.
   */
  className?: string;
}

/**
 * Large, mobile-first tap target for selecting a row inside a clickable card.
 *
 * The whole card usually has an onClick that opens the item. A bare checkbox is
 * a ~14px target, so on touch screens users miss it, hit the card, and open the
 * item — losing their selection. This wraps the checkbox in a full-height
 * <label> that:
 *   - gives a generous (≥44px) touch area — tapping anywhere in the zone toggles
 *     the checkbox via native <label> behaviour;
 *   - stops the click from bubbling to the card, so selecting never opens the item;
 *   - shows a right divider so the "select" zone reads as distinct from the content.
 */
export default function RowSelectZone({
  checked,
  onToggle,
  ariaLabel = 'Select row',
  className = '-my-3.5 py-3.5 -ml-4 sm:-ml-5 pl-4 sm:pl-5 pr-3.5',
}: RowSelectZoneProps) {
  return (
    <label
      onClick={e => e.stopPropagation()}
      className={`flex items-center self-stretch flex-shrink-0 cursor-pointer touch-manipulation border-r border-gray-200 ${className}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        aria-label={ariaLabel}
        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-5 w-5"
      />
    </label>
  );
}
